import { ClassValue, clsx } from 'clsx';
import { Button } from '@shared/components/Button';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { AskVoiceVisualizer, UseAskDictationResult } from '../voice';
import styles from './AskDictationBar.module.css';

const VISUALIZER_HEIGHT = grid(3);

interface AskDictationBarProps {
  dictation: UseAskDictationResult;
  /**
   * How the caller wants the bar to size itself. The composer gives it its own full-width row above
   * the input; Capture puts it in a slot beside the controls, where it has to be told to take the
   * slack or the meter inside it has none to take.
   */
  className?: ClassValue;
}

/**
 * The dictation controls, on their own row above the input.
 *
 * Not in the row of buttons beside the input, where they started: the meter and two controls
 * competed with the input for width and squeezed it to nothing. Out here they have the full width
 * and the input keeps its own.
 *
 * Only the meter and the two controls, deliberately. The words being dictated are already going
 * into the input, which is the feedback that matters, so a label repeating "Listening" beside it
 * would be noise. It stays for screen readers, which have no meter to watch.
 */
export function AskDictationBar({ dictation, className }: AskDictationBarProps) {
  const { isActive, status, stream, finish, discard } = dictation;
  if (!isActive) {
    return null;
  }
  const isWaitingForPermission = status === 'requesting';

  return (
    <div className={clsx(styles.bar, className)} data-testid="ask-dictation-bar">
      <span className={styles.srOnly} role="status">
        {i18n('ask.voice.listening')}
      </span>
      <AskVoiceVisualizer stream={stream} height={VISUALIZER_HEIGHT} />
      {/* Reads as one pair with the filled button beside it: both are `size="small"`, a 3Q box that
          `iconOnly` rounds to a circle, so the same 2.5Q of icon sits in the same ring in both.
          `exit` rather than `remove-x` because that ring is the button's — an icon carrying its own
          circle would draw a second one. */}
      <Button
        variant="tertiary"
        size="small"
        icon="exit"
        iconOnly
        iconGridSize={2.5}
        label={i18n('ask.voice.cancel')}
        onClick={discard}
        testID="ask-dictate-cancel"
      />
      <Button
        variant="primary"
        size="small"
        icon="check"
        iconOnly
        iconGridSize={2.5}
        label={i18n('ask.voice.stop')}
        disabled={isWaitingForPermission}
        onClick={finish}
        testID="ask-dictate-stop"
      />
    </div>
  );
}
